/**
 * @owner @Appboy/messaging-experience
 */

import sharedConstants from "@lib/shared_constants";
import type { SHARED_CONSTANTS_CAMPAIGN_MESSAGE_TYPES } from "@lib/shared_constants";

import type { TypeFromArray, ValueOf } from "./typeHelpers";

const { CAMPAIGN_MESSAGE_TYPES } = sharedConstants;

export default CAMPAIGN_MESSAGE_TYPES;

export type MessageType = ValueOf<SHARED_CONSTANTS_CAMPAIGN_MESSAGE_TYPES>;

export const InAppMessageSubTypes = [
  /** A small section at top or bottom of screen covering width of the screen */
  "SLIDEUP",
  /** A modal in middle of the screen that darkens the rest of the screen */
  "MODAL",
  /** An interstitial taking up the whole screen (modalized on larger devices) */
  "FULL",
  /** Unified zipless cross-platform HTML view taking up the whole screen */
  "HTML",
  /** Legacy - A native-platform-only HTML view taking up the whole screen */
  "HTML_FULL",
  /** Legacy - A web-only zipless HTML_FULL */
  "WEB_HTML",
  /** like MODAL, but with support for the css field */
  "MODAL_STYLED",
  /** Delivered as a HTML message with content generated by the html-iam-templating-engine */
  "SURVEY",
  /** Delivered as a HTML message with content generated by the html-iam-templating-engine */
  "EMAIL_CAPTURE",
] as const;

export type InAppMessageSubtype = TypeFromArray<typeof InAppMessageSubTypes>;

export type EmailSubtype = "AMP";

export type SMSSubtype = "TWILIO";

export type ContentCardSubtype = "CLASSIC" | "CAPTIONED_IMAGE" | "BANNER" | "CONTROL";

export type MessageSubtype = InAppMessageSubtype | EmailSubtype | SMSSubtype | ContentCardSubtype;

/**
 * message types that are also push
 */
export const pushTypes = [
  CAMPAIGN_MESSAGE_TYPES.ANDROID_PUSH,
  CAMPAIGN_MESSAGE_TYPES.IOS_PUSH,
  CAMPAIGN_MESSAGE_TYPES.KINDLE_PUSH,
  CAMPAIGN_MESSAGE_TYPES.WEB_PUSH,
] as const;

export type PushType = TypeFromArray<typeof pushTypes>;

export const isPushType = (type: MessageType): type is PushType => {
  return pushTypes.includes(type as any);
};

/**
 * describes the types of messages that can be added to multi-channel and/or multi-variate
 * message-bearing objects
 */
export type SelectableMessageType = Exclude<MessageType, "slideup" | "control" | "featureFlag">;

export const isSelectableMessageType = (messageType: MessageType): messageType is SelectableMessageType => {
  return messageType !== "slideup" && messageType !== "control" && messageType !== "featureFlag";
};

/**
 * Channels that don't have per-channel reachability stats and instead use
 * total segment users for their audience count.
 */
export const channelsThatTakeTotalStat = [
  CAMPAIGN_MESSAGE_TYPES.WEBHOOK,
  CAMPAIGN_MESSAGE_TYPES.SLIDEUP,
  CAMPAIGN_MESSAGE_TYPES.TRIGGER_IAM,
  CAMPAIGN_MESSAGE_TYPES.CONTENT_CARD,
  CAMPAIGN_MESSAGE_TYPES.FEATURE_FLAG_EXPERIMENT,
] as const;

export type ChannelThatTakesTotalStat = TypeFromArray<typeof channelsThatTakeTotalStat>;

export const isChannelThatTakesTotalStat = (channel: MessageType): channel is ChannelThatTakesTotalStat => {
  return (channelsThatTakeTotalStat as readonly MessageType[]).includes(channel);
};
